import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Cake, Trophy, Sparkles, Calendar, PartyPopper, ChevronRight, Megaphone, Send, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('staff_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

const whenLabel = (daysAway, isToday) => {
  if (isToday) return 'Today';
  if (daysAway === 1) return 'Tomorrow';
  return `In ${daysAway} day${daysAway === 1 ? '' : 's'}`;
};

/**
 * Celebrations pulse bar — shows birthdays + hire-anniversaries landing in the next 7 days.
 * Each row has a one-tap Shoutout button that posts into the staff Announcements channel.
 */
export const CelebrationsPulse = ({ onOpenStaff }) => {
  const [data, setData] = useState(null);
  const [shoutoutTarget, setShoutoutTarget] = useState(null); // {username, display_name, kind, years}
  const [shoutoutMsg, setShoutoutMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(new Set()); // Lock button once used per session

  useEffect(() => {
    const load = async () => {
      const h = getAuthHeaders(); if (!h) return;
      try {
        const r = await axios.get(`${BACKEND_URL}/api/admin/people/celebrations`, { headers: h });
        setData(r.data);
      } catch {}
    };
    load();
    const t = setInterval(load, 60 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const openShoutout = (person, kind) => {
    const auto = kind === 'birthday'
      ? `🎂 Everyone wish @${person.username} a very happy birthday today! 🎉`
      : `🏆 Celebrating ${person.years} year${person.years === 1 ? '' : 's'} with Starnight for @${person.username} today! Thanks for everything, ${person.display_name} 💫`;
    setShoutoutTarget({ ...person, kind });
    setShoutoutMsg(auto);
  };

  const sendShoutout = async () => {
    if (!shoutoutTarget || !shoutoutMsg.trim()) return;
    setSending(true);
    try {
      const h = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/people/shoutout`, {
        target_username: shoutoutTarget.username,
        kind: shoutoutTarget.kind,
        message: shoutoutMsg,
        years: shoutoutTarget.years,
      }, { headers: h });
      toast.success(`Shoutout posted for ${shoutoutTarget.display_name} 🎉`);
      setSent(prev => new Set(prev).add(`${shoutoutTarget.username}-${shoutoutTarget.kind}`));
      setShoutoutTarget(null); setShoutoutMsg('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Shoutout failed'); }
    setSending(false);
  };

  if (!data || data.total === 0) return null;

  const birthdays = data.birthdays || [];
  const anniversaries = data.anniversaries || [];

  return (
    <div className="relative overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/[0.06] via-rose-500/[0.04] to-violet-500/[0.06] p-4"
      data-testid="celebrations-pulse">
      <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-amber-400/[0.10] blur-[80px]" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-rose-500/[0.08] blur-[80px]" />

      <div className="relative flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg border border-amber-400/40 bg-amber-500/15 flex items-center justify-center">
          <PartyPopper className="w-3.5 h-3.5 text-amber-300" />
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-amber-300/80 font-semibold">Celebrations · this week</p>
          <p className="text-white text-sm font-medium">{data.total} reason{data.total === 1 ? '' : 's'} to celebrate — tap <Megaphone className="inline w-3 h-3 text-amber-300 mx-0.5" /> to shoutout the team</p>
        </div>
        <Sparkles className="w-3 h-3 text-amber-300 ml-auto animate-pulse" />
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-2">
        {birthdays.map(b => {
          const key = `${b.username}-birthday`;
          const didSend = sent.has(key);
          return (
            <div key={`b-${b.username}`}
              data-testid={`celebration-birthday-${b.username}`}
              className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-rose-400/20 hover:border-rose-400/50 hover:bg-white/[0.05] transition-all group">
              <button onClick={() => onOpenStaff && onOpenStaff(b.username)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center flex-shrink-0 relative">
                  <Cake className="w-4 h-4 text-white" />
                  {b.is_today && <><span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-400 animate-ping" /><span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-400" /></>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-white text-[12.5px] font-semibold truncate">{b.display_name}</p>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-rose-500/15 border border-rose-400/35 text-rose-200">Birthday</span>
                    {b.turning && <span className="text-white/50 text-[10px]">→ {b.turning}</span>}
                  </div>
                  <p className="text-white/50 text-[10.5px] mt-0.5 flex items-center gap-1">
                    <Calendar className="w-2.5 h-2.5" />
                    {whenLabel(b.days_away, b.is_today)}{b.department ? ` · ${b.department}` : ''}
                  </p>
                </div>
              </button>
              <button onClick={() => openShoutout(b, 'birthday')} disabled={didSend}
                data-testid={`shoutout-birthday-${b.username}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-semibold transition-all flex-shrink-0"
                style={didSend
                  ? { background: '#10B98118', borderColor: '#10B98140', color: '#10B981' }
                  : { background: '#F43F5E18', borderColor: '#F43F5E40', color: '#FDA4AF' }}>
                <Megaphone className="w-2.5 h-2.5" /> {didSend ? 'Posted' : 'Shoutout'}
              </button>
            </div>
          );
        })}

        {anniversaries.map(a => {
          const key = `${a.username}-anniversary`;
          const didSend = sent.has(key);
          return (
            <div key={`a-${a.username}`}
              data-testid={`celebration-anniversary-${a.username}`}
              className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-amber-400/20 hover:border-amber-400/50 hover:bg-white/[0.05] transition-all group">
              <button onClick={() => onOpenStaff && onOpenStaff(a.username)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 relative">
                  <Trophy className="w-4 h-4 text-white" />
                  {a.is_today && <><span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 animate-ping" /><span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400" /></>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-white text-[12.5px] font-semibold truncate">{a.display_name}</p>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-amber-500/15 border border-amber-400/35 text-amber-200">{a.years} yr{a.years === 1 ? '' : 's'}</span>
                  </div>
                  <p className="text-white/50 text-[10.5px] mt-0.5 flex items-center gap-1">
                    <Calendar className="w-2.5 h-2.5" />
                    {whenLabel(a.days_away, a.is_today)}{a.department ? ` · ${a.department}` : ''}
                  </p>
                </div>
              </button>
              <button onClick={() => openShoutout(a, 'anniversary')} disabled={didSend}
                data-testid={`shoutout-anniversary-${a.username}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-semibold transition-all flex-shrink-0"
                style={didSend
                  ? { background: '#10B98118', borderColor: '#10B98140', color: '#10B981' }
                  : { background: '#F59E0B18', borderColor: '#F59E0B40', color: '#FCD34D' }}>
                <Megaphone className="w-2.5 h-2.5" /> {didSend ? 'Posted' : 'Shoutout'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Shoutout composer modal */}
      {shoutoutTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShoutoutTarget(null)}>
          <div className="max-w-md w-full rounded-2xl border border-amber-400/40 bg-[#0a0a14] p-5" onClick={(e) => e.stopPropagation()} data-testid="shoutout-modal">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-400/40 flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.2em] uppercase text-amber-300/80 font-semibold">Team-wide shoutout</p>
                <h3 className="text-white text-base font-semibold truncate">
                  {shoutoutTarget.kind === 'birthday' ? `${shoutoutTarget.display_name}'s birthday` : `${shoutoutTarget.display_name}'s ${shoutoutTarget.years}-yr anniversary`}
                </h3>
              </div>
              <button onClick={() => setShoutoutTarget(null)} className="w-7 h-7 rounded-md text-white/40 hover:text-white hover:bg-white/[0.06] flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-white/50 text-xs mb-2">Posts to the staff Announcements channel. You can tweak the wording.</p>
            <textarea value={shoutoutMsg} onChange={(e) => setShoutoutMsg(e.target.value)}
              rows={4} data-testid="shoutout-message-input"
              className="w-full px-3 py-2 bg-black/40 border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-amber-400/50 resize-none" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setShoutoutTarget(null)}
                className="px-3 py-1.5 rounded-lg border border-white/[0.08] text-white/60 hover:bg-white/[0.04] text-xs">Cancel</button>
              <button onClick={sendShoutout} disabled={sending || !shoutoutMsg.trim()}
                data-testid="shoutout-send-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-400/40 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 text-xs font-semibold disabled:opacity-50">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Post to team
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CelebrationsPulse;
